import React, { useEffect, useState } from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  SelectInput,
  Toolbar,
  SaveButton,
  SimpleShowLayout,
  FunctionField,
  useNotify,
  useRedirect,
  useDataProvider,
  required,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Typography, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { formatDateToMMDDYYYY, fromTimestamp } from '../../utils/helpers'
import PhoneNumberInput from '../common/PhoneNumberInput'
import { client } from '../../providers/data/client'

// ---------------------------------------------------------------------------
// Helpers (mirrors KitRequestForm.js)
// ---------------------------------------------------------------------------
const isFutureDate = (dateString) => {
  if (!dateString) return false
  const selected = new Date(dateString)
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return selected > today
}

const calculateAge = (dobString) => {
  if (!dobString) return 0
  const dob = new Date(dobString)
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }
  return age
}

const parseAlphaOnly = (value) => (value ? value.replace(/[^A-Za-z\s]/g, '') : '')

// DateInput display/submit for dateOfVasectomy & patientDob:
// - format: strip a stored value (plain or full-ISO) down to YYYY-MM-DD for
//   the native date picker, without ever going through a timezone-sensitive
//   Date parse (mirrors new-admin's KitRequestEdit.js formatDateValue).
// - parse: re-encode the picker's plain YYYY-MM-DD back to a UTC-midnight ISO
//   timestamp on submit, matching how KitRequestForm.js stores these fields.
const formatDateValue = (value) => {
  if (!value) return ''
  return String(value).slice(0, 10)
}

const parseDateValue = (value) => {
  if (!value) return value
  return new Date(value).toISOString()
}

// ---------------------------------------------------------------------------
// Form-level validator — mirrors KitRequestForm.js validate()
// Returns an object of field → error message (react-admin format)
// ---------------------------------------------------------------------------
const validateKitRequest = (values) => {
  const errors = {}

  // Required fields
  const requiredFields = [
    'providerLocationId',
    'providerName',
    'dateOfVasectomy',
    'patientFirstName',
    'patientLastName',
    'patientDob',
    'shippingAddress1',
    'shippingCity',
    'shippingState',
    'shippingZip',
    'notificationMethod',
    'email',
    'phoneNumber',
  ]

  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = 'This field is required'
    }
  })

  // Date of Birth — no future dates, patient must be at least 16
  if (values.patientDob) {
    if (isFutureDate(values.patientDob)) {
      errors.patientDob = 'Date cannot be in the future'
    } else if (calculateAge(values.patientDob) < 16) {
      errors.patientDob = 'Patient must be at least 16 years old'
    }
  }

  // ZIP code — flexible for US / Canada
  if (values.shippingZip) {
    const zipRegex = /^[A-Za-z0-9\s-]{3,10}$/
    if (!zipRegex.test(values.shippingZip)) {
      errors.shippingZip = 'Please enter a valid postal code'
    }
  }

  // Email format
  if (values.email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!emailRegex.test(values.email)) {
      errors.email = 'Invalid email address'
    }
  }

  // Phone format/country is validated by PhoneNumberInput (only when changed)

  return errors
}

// ---------------------------------------------------------------------------
// Server-side Error Handler (Integrates Google Address Validation errors)
// ---------------------------------------------------------------------------
const ServerErrorHandler = ({ serverErrors }) => {
  const { setError } = useFormContext()

  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length > 0) {
      // Small timeout ensures react-hook-form is ready to accept errors
      setTimeout(() => {
        Object.entries(serverErrors).forEach(([field, msg]) => {
          setError(field, { type: 'manual', message: msg }, { shouldFocus: true })
        })
      }, 100)
    }
  }, [serverErrors, setError])

  return null
}

// ---------------------------------------------------------------------------
// Clinic & Doctor dynamic dependent dropdowns
// ---------------------------------------------------------------------------
const ClinicDoctorSelects = () => {
  const { setValue } = useFormContext()
  const providerGroupId = useWatch({ name: 'providerGroupId' })
  const providerLocationId = useWatch({ name: 'providerLocationId' })
  const providerName = useWatch({ name: 'providerName' })

  const [clinics, setClinics] = useState([])
  const [providers, setProviders] = useState([])
  const [isLoadingClinics, setIsLoadingClinics] = useState(false)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)

  // Fetch Clinics on Mount / Group Change
  useEffect(() => {
    const fetchClinics = async () => {
      if (!providerGroupId) return
      setIsLoadingClinics(true)
      try {
        const locationsResponse = await client().get(`/provider-portal/provider-locations`)
        if (locationsResponse.data?.data) {
          const locations = locationsResponse.data.data
          const groupLocations = locations.filter((loc) => loc.providerGroupId === providerGroupId)
          const combinedClinics = groupLocations.map((loc) => ({
            id: loc.id,
            providerGroupId: loc.providerGroupId,
            name: loc.locationName,
            legacyClinicName: loc.locationName,
          }))
          setClinics(combinedClinics)
        }
      } catch (err) {
        console.error('Error fetching clinics:', err)
      } finally {
        setIsLoadingClinics(false)
      }
    }
    fetchClinics()
  }, [providerGroupId])

  // Fetch Providers when Location changes
  useEffect(() => {
    const fetchProviders = async () => {
      if (!providerLocationId) {
        setProviders([])
        return
      }
      setIsLoadingProviders(true)
      try {
        const response = await client().get(`/provider-portal/providers?_providerLocationId=${providerLocationId}`)
        if (response.data?.data) {
          const fetchedProviders = response.data.data.map((p) => ({
            id: p.displayName, // providerName is stored as the provider's displayName
            providerId: p.id,
            name: p.displayName,
          }))
          setProviders(fetchedProviders)
        }
      } catch (err) {
        console.error('Error fetching providers:', err)
      } finally {
        setIsLoadingProviders(false)
      }
    }
    fetchProviders()
  }, [providerLocationId])

  // Sync related form fields when location changes
  useEffect(() => {
    if (providerLocationId && clinics.length > 0) {
      const selectedClinic = clinics.find((c) => c.id === providerLocationId)
      if (selectedClinic) {
        setValue('providerGroupId', selectedClinic.providerGroupId, { shouldValidate: true })
        setValue('clinicName', selectedClinic.legacyClinicName, { shouldValidate: true })
      }
    }
  }, [providerLocationId, clinics, setValue])

  // Sync providerId when providerName changes
  useEffect(() => {
    if (providerName && providers.length > 0) {
      const selectedProvider = providers.find((p) => p.id === providerName)
      if (selectedProvider) {
        setValue('providerId', selectedProvider.providerId, { shouldValidate: true })
      }
    }
  }, [providerName, providers, setValue])

  return (
    <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
      <SelectInput
        label="Provider Location"
        source="providerLocationId"
        choices={clinics}
        isLoading={isLoadingClinics}
        validate={required()}
        sx={{ flex: 1, minWidth: 240 }}
      />
      <SelectInput
        label="Provider Name"
        source="providerName"
        choices={providers}
        isLoading={isLoadingProviders}
        disabled={!providerLocationId || isLoadingProviders}
        validate={required()}
        sx={{ flex: 1, minWidth: 240 }}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Read-only summary section
// ---------------------------------------------------------------------------
const KitRequestSummary = () => (
  <SimpleShowLayout style={{ paddingLeft: 0 }} spacing={2}>
    <FunctionField
      label="Request ID"
      render={(record) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {record.requestId}
        </Typography>
      )}
    />
    <FunctionField label="Status" render={(record) => record.requestStatus || '—'} />
    <FunctionField label="Date Created" render={(record) => fromTimestamp(record.createdAt) || '—'} />

    <FunctionField label="Target Convert Date" render={(record) => formatDateToMMDDYYYY(record.targetConvertDate) || '—'} />
  </SimpleShowLayout>
)

// ---------------------------------------------------------------------------
// Custom toolbar
// ---------------------------------------------------------------------------
const KitRequestEditToolbar = ({ onBack }) => (
  <Toolbar sx={{ display: 'flex', gap: 2 }}>
    <SaveButton label="Save Changes" />
    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
      Back to History
    </Button>
  </Toolbar>
)

// ---------------------------------------------------------------------------
// Main Edit component
// ---------------------------------------------------------------------------
export const KitRequestEdit = () => {
  const notify = useNotify()
  const redirect = useRedirect()
  const dataProvider = useDataProvider()
  const [serverErrors, setServerErrors] = useState(null)
  const [addressConfirmation, setAddressConfirmation] = useState(null)

  const today = new Date()
  const maxYear = today.getFullYear() - 16
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  const maxDobString = `${maxYear}-${month}-${day}`

  const handleBack = () => {
    redirect('/provider-portal/kit-requests?tab=request_history')
  }

  // The API recomputes targetConvertDate whenever the Date of Vasectomy changes, so the
  // stored value can differ from what the form submitted. Fold that into the save
  // confirmation — one toast, since react-admin queues notifications one at a time.
  const handleSuccess = (data) => {
    setServerErrors(null)
    const message = data?.targetConvertDateRecalculated
      ? `Kit request updated. Target Convert Date recalculated to ${formatDateToMMDDYYYY(data.targetConvertDate)} from the new Date of Vasectomy.`
      : 'Kit request updated successfully'
    notify(message, { type: 'success' })
    redirect('/provider-portal/kit-requests?tab=request_history')
  }

  const handleError = (error) => {
    const responseData = error?.response?.data
    if (responseData?.fieldErrors && Object.keys(responseData.fieldErrors).length > 0) {
      setServerErrors(responseData.fieldErrors)
    }
    const msg = responseData?.message || error?.body?.message || 'Failed to update kit request'
    notify(msg, { type: 'error' })
  }

  const closeAddressConfirmation = () => {
    setAddressConfirmation(null)
  }

  // Retry the update directly via the data provider with an explicit override
  // flag, bypassing SimpleForm's controlled submit so the user doesn't have to
  // re-enter or resubmit anything. Used both when Google couldn't verify the
  // address at all, and when the user wants to keep their entry over a suggestion.
  const useAddressAsEntered = async () => {
    if (!addressConfirmation) return
    const { resource, id, data, previousData } = addressConfirmation
    setAddressConfirmation(null)
    try {
      const result = await dataProvider.update(resource, {
        id,
        data: { ...data, addressConfirmationAccepted: true },
        previousData,
      })
      handleSuccess(result?.data)
    } catch (error) {
      handleError(error)
    }
  }

  const useSuggestedAddress = async () => {
    if (!addressConfirmation?.suggestedAddress) return
    const { resource, id, data, previousData, suggestedAddress } = addressConfirmation
    setAddressConfirmation(null)
    try {
      const result = await dataProvider.update(resource, {
        id,
        data: { ...data, ...suggestedAddress, addressConfirmationAccepted: true },
        previousData,
      })
      handleSuccess(result?.data)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <>
      <Edit
        resource="provider-portal/kit-requests"
        mutationMode="pessimistic"
        mutationOptions={{
          onSuccess: handleSuccess,
          onError: (error, variables) => {
            const responseData = error?.response?.data
            if (responseData?.requiresAddressConfirmation) {
              setAddressConfirmation({
                message: responseData?.message,
                enteredAddress: responseData?.enteredAddress || {},
                suggestedAddress: responseData?.suggestedAddress || null,
                unverified: Boolean(responseData?.addressUnverified),
                resource: variables?.resource,
                id: variables?.id,
                data: variables?.data,
                previousData: variables?.previousData,
              })
              return
            }
            handleError(error)
          },
        }}
      >
        <SimpleForm validate={validateKitRequest} toolbar={<KitRequestEditToolbar onBack={handleBack} />}>
          <ServerErrorHandler serverErrors={serverErrors} />

          {/* Read-only summary */}
          <KitRequestSummary />

          <Divider sx={{ width: '100%', my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Clinic &amp; Provider
          </Typography>

          {/* Clinic / Doctor — Dependent Dropdowns */}
          <ClinicDoctorSelects />

          <Divider sx={{ width: '100%', my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Vasectomy &amp; Patient Information
          </Typography>

          {/* Date of Vasectomy — future dates allowed per KitRequestForm comment */}
          <DateInput
            label="Date of Vasectomy"
            source="dateOfVasectomy"
            format={formatDateValue}
            parse={parseDateValue}
            sx={{ width: 280 }}
          />

          {/* Patient name */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
            <TextInput
              label="Patient First Name"
              source="patientFirstName"
              parse={parseAlphaOnly}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextInput label="Patient Last Name" source="patientLastName" parse={parseAlphaOnly} sx={{ flex: 1, minWidth: 200 }} />
          </Box>

          {/* Date of Birth */}
          <DateInput
            label="Date of Birth"
            source="patientDob"
            format={formatDateValue}
            parse={parseDateValue}
            inputProps={{ max: maxDobString }}
            sx={{ width: 280 }}
          />

          <Divider sx={{ width: '100%', my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Shipping Address
          </Typography>

          <TextInput label="Street Address" source="shippingAddress1" fullWidth />
          <TextInput label="Apt / Suite (optional)" source="shippingAddress2" fullWidth />
          <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
            <TextInput label="City" source="shippingCity" sx={{ flex: 2, minWidth: 180 }} />
            <TextInput label="State" source="shippingState" sx={{ flex: 1, minWidth: 100 }} />
            <TextInput label="ZIP Code" source="shippingZip" sx={{ flex: 1, minWidth: 120 }} />
          </Box>

          <Divider sx={{ width: '100%', my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Contact &amp; Notifications
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
            <PhoneNumberInput label="Phone Number" source="phoneNumber" fullWidth sx={{ flex: 1, minWidth: 200 }} />
            <TextInput label="Email" source="email" type="email" sx={{ flex: 1, minWidth: 200 }} />
          </Box>

          <SelectInput
            label="Notification Preference"
            source="notificationMethod"
            validate={required()}
            choices={[
              { id: 'Email', name: 'Email' },
              { id: 'Text', name: 'Text' },
            ]}
            sx={{ width: 280 }}
          />
        </SimpleForm>
      </Edit>

      <Dialog open={Boolean(addressConfirmation)} onClose={closeAddressConfirmation} maxWidth="sm" fullWidth>
        <DialogTitle>
          {addressConfirmation?.unverified ? "We Couldn't Verify This Address" : 'Confirm Shipping Address'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {addressConfirmation?.message ||
              (addressConfirmation?.unverified
                ? "We weren't able to verify this address. It may be a new address that isn't in postal records yet. You can go back and double-check it, or save it exactly as entered."
                : 'We found a recommended shipping address. Please confirm to continue.')}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {addressConfirmation?.unverified ? 'Address entered' : 'You entered'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: addressConfirmation?.suggestedAddress ? 2 : 0 }}>
            {[
              addressConfirmation?.enteredAddress?.shippingAddress1,
              addressConfirmation?.enteredAddress?.shippingAddress2,
              `${addressConfirmation?.enteredAddress?.shippingCity || ''}, ${addressConfirmation?.enteredAddress?.shippingState || ''} ${addressConfirmation?.enteredAddress?.shippingZip || ''}`.trim(),
            ]
              .filter(Boolean)
              .join('\n')}
          </Typography>

          {addressConfirmation?.suggestedAddress && (
            <>
              <Typography variant="subtitle2">Suggested address</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {[
                  addressConfirmation.suggestedAddress?.shippingAddress1,
                  addressConfirmation.suggestedAddress?.shippingAddress2,
                  `${addressConfirmation.suggestedAddress?.shippingCity || ''}, ${addressConfirmation.suggestedAddress?.shippingState || ''} ${addressConfirmation.suggestedAddress?.shippingZip || ''}`.trim(),
                ]
                  .filter(Boolean)
                  .join('\n')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {addressConfirmation?.unverified ? (
            <>
              <Button onClick={closeAddressConfirmation}>Go back and edit</Button>
              <Button variant="contained" onClick={useAddressAsEntered}>
                Use address as entered
              </Button>
            </>
          ) : (
            <>
              <Button onClick={useAddressAsEntered}>Keep entered address</Button>
              <Button variant="contained" onClick={useSuggestedAddress}>
                Use suggested address
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default KitRequestEdit
